import { FiExternalLink, FiRefreshCw } from "react-icons/fi";
import { Link } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCents } from "@/lib/utils";
import type { OICSummary } from "@/features/oic/types";

interface Props {
  clientId: string;
  caseId: string;
  summary: OICSummary | null;
  recomputing: boolean;
  onRecompute: () => void;
}

/**
 * Step 1 — Financials check. A read-only rollup of the Tier 2.2 worksheet offer
 * figures (RMI, total asset equity, total tax due). No editing here — the
 * worksheet owns the inputs; the "Open Financial Worksheet" seam deep-links to
 * its offer tab. Recompute re-reads the offer endpoint and snapshots the flow.
 */
export function FinancialsStep({ clientId, caseId, summary, recomputing, onRecompute }: Props) {
  const worksheetLink = `/client/${clientId}/cases/${caseId}/worksheets/offer`;

  if (!summary?.available) {
    return (
      <div className="grid gap-4">
        <Alert>
          <AlertTitle>No financial worksheet yet</AlertTitle>
          <AlertDescription>
            The offer analysis reads the client's financial worksheet. Complete it
            first, then recompute here.
          </AlertDescription>
        </Alert>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to={worksheetLink}>
              <FiExternalLink className="size-4" /> Open Financial Worksheet
            </Link>
          </Button>
          <Button onClick={onRecompute} disabled={recomputing}>
            <FiRefreshCw className="size-4" /> Recompute
          </Button>
        </div>
      </div>
    );
  }

  const rows: { label: string; cents: number | undefined }[] = [
    { label: "Reasonable Monthly Income (RMI)", cents: summary.rmiCents },
    { label: "Total quick-sale asset equity", cents: summary.totalAssetsCents },
    { label: "Total tax due", cents: summary.taxDueCents },
  ];

  return (
    <div className="grid gap-4">
      <div className="grid gap-4 sm:grid-cols-3">
        {rows.map((r) => (
          <Card key={r.label}>
            <CardHeader className="pb-1">
              <CardTitle className="text-muted-foreground text-xs font-medium">
                {r.label}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-2xl font-semibold tabular-nums">
              {r.cents != null ? formatCents(r.cents) : "—"}
            </CardContent>
          </Card>
        ))}
      </div>
      <p className="text-muted-foreground text-sm">
        Collection statute horizon:{" "}
        <span className="text-foreground font-medium">{summary.csedMonths ?? "—"} months</span>.
        These figures come from the financial worksheet and are read-only here.
      </p>
      <div className="flex gap-2">
        <Button asChild variant="outline">
          <Link to={worksheetLink}>
            <FiExternalLink className="size-4" /> Open Financial Worksheet
          </Link>
        </Button>
        <Button onClick={onRecompute} disabled={recomputing}>
          <FiRefreshCw className="size-4" /> Recompute
        </Button>
      </div>
    </div>
  );
}
